import React, { ReactNode, use, useEffect } from 'react';
import { TFunction } from 'i18next';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import useTableFiltering, { TableFiltering } from 'shared/components/table/hooks/table-filtering';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';
import { jsonParseWithLowerCaseKeys } from 'shared/utils/string-utils';

import ReadResidentsChangeLogResponse from 'features/residents/models/read-residents-change-log-response';
import ResidentChangeLog from 'features/residents/models/resident-change-log';
import { ResidentChangeLogType } from 'features/residents/models/resident-change-log-type';
import { useReadResidentsChangeLog } from 'features/residents/resident-change-logs/providers/residents-change-log-queries';
import useResidentsChangeLogFiltersState, {
  ResidentsChangeLogFiltersState,
} from 'features/residents/resident-change-logs/providers/states/residents-change-log-filters.state';

export type ResidentsChangeLogState = {
  residentsChangeLog: ReadResidentsChangeLogResponse | undefined;
  residentsChangeLogIsLoading: boolean;
  filtersState: ResidentsChangeLogFiltersState;
  table: TableFiltering<ResidentChangeLog>;
  refresh: () => void;

  valueColumn: (
    item: ResidentChangeLog,
    t: TFunction<'residentsChangeLog'>,
  ) => { oldValueDisplay: string | ReactNode | undefined; newValueDisplay: string | ReactNode | undefined };
};

const useResidentsChangeLogState = (): ResidentsChangeLogState => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const table = useTableFiltering<ResidentChangeLog>({
    defaultSort: {
      isAscending: true,
      property: 'time',
    },
    sessionStorageKey: FilterSessionStorageKeys.RESIDENT_SETTING_CHANGE_LOG_SORT,
  });

  const filtersState = useResidentsChangeLogFiltersState({ table });

  const {
    data: residentsChangeLog,
    isLoading: residentsChangeLogIsLoading,
    error: residentsChangeLogError,
    refetch: residentsChangeLogRefetch,
  } = useReadResidentsChangeLog(filtersState.getResidentsChangeLogQuery);

  useEffect(() => {
    if (!residentsChangeLogError) return;

    setErrorMessage(residentsChangeLogError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residentsChangeLogError]);

  const getChangeLogDisplayValues = (
    item: ResidentChangeLog,
    t: TFunction<'residentsChangeLog'>,
  ): { oldValueDisplay: string | ReactNode | undefined; newValueDisplay: string | ReactNode | undefined } => {
    switch (item.type) {
      case ResidentChangeLogType.InformationAdded:
        return { oldValueDisplay: undefined, newValueDisplay: item.newValueDisplay };

      case ResidentChangeLogType.InformationDeleted:
        return { oldValueDisplay: item.oldValueDisplay, newValueDisplay: undefined };

      case ResidentChangeLogType.DeviceLinked:
        return { oldValueDisplay: undefined, newValueDisplay: t('residentsChangeLogTable.value.linked') };

      case ResidentChangeLogType.DeviceUnlinked:
        return { oldValueDisplay: t('residentsChangeLogTable.value.linked'), newValueDisplay: t('residentsChangeLogTable.value.unlinked') };

      case ResidentChangeLogType.ScenarioActivated:
        return {
          oldValueDisplay: t('residentsChangeLogTable.value.deactivated'),
          newValueDisplay: t('residentsChangeLogTable.value.activated'),
        };

      case ResidentChangeLogType.ScenarioDeactivated:
        return {
          oldValueDisplay: t('residentsChangeLogTable.value.activated'),
          newValueDisplay: t('residentsChangeLogTable.value.deactivated'),
        };

      case ResidentChangeLogType.ScenarioNotificationTypeChanged: {
        return {
          oldValueDisplay: item.oldValueDisplay,
          newValueDisplay: item.newValueDisplay,
        };
      }

      case ResidentChangeLogType.ScenarioAlwaysActiveEnabled:
        return {
          oldValueDisplay: t('residentsChangeLogTable.value.alwaysActiveDisabled'),
          newValueDisplay: t('residentsChangeLogTable.value.alwaysActiveEnabled'),
        };
      case ResidentChangeLogType.ScenarioAlwaysActiveDisabled:
        return {
          oldValueDisplay: t('residentsChangeLogTable.value.alwaysActiveEnabled'),
          newValueDisplay: t('residentsChangeLogTable.value.alwaysActiveDisabled'),
        };

      case ResidentChangeLogType.ScenarioTimeFramesUpdated:
        return { oldValueDisplay: renderTimeFrames(item.oldValueDisplay, t), newValueDisplay: renderTimeFrames(item.newValueDisplay, t) };

      case ResidentChangeLogType.ScenarioSendAlarmTimeChanged:
        return {
          oldValueDisplay: item.oldValueDisplay ? t(`residentsChangeLogTable.value.alarmTimeOut.${item.oldValueDisplay}`) : undefined,
          newValueDisplay: item.newValueDisplay ? t(`residentsChangeLogTable.value.alarmTimeOut.${item.newValueDisplay}`) : undefined,
        };

      case ResidentChangeLogType.AcousticTemporaryOverwriteEnabled:
      case ResidentChangeLogType.VideoPrivacyModeEnabled:
        return {
          oldValueDisplay: t('residentsChangeLogTable.value.off'),
          newValueDisplay: t('residentsChangeLogTable.value.on'),
        };

      case ResidentChangeLogType.AcousticTemporaryOverwriteDisabled:
      case ResidentChangeLogType.VideoPrivacyModeDisabled:
        return {
          oldValueDisplay: t('residentsChangeLogTable.value.on'),
          newValueDisplay: t('residentsChangeLogTable.value.off'),
        };

      case ResidentChangeLogType.AcousticTemporaryOverwriteConfigurationChanged:
        return {
          oldValueDisplay: item.oldValueDisplay,
          newValueDisplay: item.newValueDisplay,
        };

      case ResidentChangeLogType.ScenarioWhenActiveAlarmChanged: {
        return {
          oldValueDisplay: (() => {
            if (!item.oldValueDisplay) {
              return undefined;
            }
            return item.oldValueDisplay.toLowerCase() === 'true'
              ? t('residentsChangeLogTable.value.whenActiveAlarm')
              : t('residentsChangeLogTable.value.alwaysActive');
          })(),

          newValueDisplay: (() => {
            if (!item.newValueDisplay) {
              return undefined;
            }
            return item.newValueDisplay.toLowerCase() === 'true'
              ? t('residentsChangeLogTable.value.whenActiveAlarm')
              : t('residentsChangeLogTable.value.alwaysActive');
          })(),
        };
      }

      case ResidentChangeLogType.Unknown:
        return { oldValueDisplay: '-', newValueDisplay: '-' };

      case ResidentChangeLogType.AcousticThresholdChanged:
      case ResidentChangeLogType.AcousticDurationChanged:
      case ResidentChangeLogType.InformationUpdated:
      default:
        return { oldValueDisplay: item.oldValueDisplay, newValueDisplay: item.newValueDisplay };
    }
  };

  const renderTimeFrames = (jsonString: string | null | undefined, t: TFunction<'residentsChangeLog'>): React.ReactNode => {
    if (!jsonString) {
      return null;
    }

    try {
      const timeFrames = jsonParseWithLowerCaseKeys(jsonString) as { from: string; to: string }[];
      if (!Array.isArray(timeFrames)) {
        return jsonString;
      }

      return timeFrames.map((timeFrame, index) => {
        return (
          <div key={`timeFrame-${index}`}>
            {t('residentsChangeLogTable.value.timeFrameFromTo', {
              from: timeFrame.from.substring(0, 5),
              to: timeFrame.to.substring(0, 5),
            })}
          </div>
        );
      });
    } catch {
      return jsonString;
    }
  };

  const refresh = () => {
    residentsChangeLogRefetch();
    filtersState.refresh();
  };

  return {
    filtersState,
    residentsChangeLog,
    residentsChangeLogIsLoading,
    table,
    valueColumn: getChangeLogDisplayValues,
    refresh,
  };
};

export default useResidentsChangeLogState;
